import { Link, NavLink, useLocation } from "react-router-dom";
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { ReactComponent as DrawerLogo } from "../../../assets/images/drawer-logo.svg";
import { ReactComponent as ArchiveIcon } from "../../../assets/images/archive.svg";
import { ReactComponent as ConversionIcon } from "../../../assets/images/conversion.svg";
import { ReactComponent as RatingIcon } from "../../../assets/images/rating.svg";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import classes from "./index.module.scss";
import { Container } from "@mui/material";
import Header from "../components/platform/Header";
import Footer from "../components/Footer";
import useMixpanel from "../../../hooks/useMixpanel";
import { mixpanelStudentPlatform } from "../../../constants/mixpanel";
import User from "../../Common/User";
import { AuthContext } from "../../../context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

const drawerWidth = 264;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: theme.spacing(11),
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(3),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
  "& .MuiDrawer-paper": {
    position: "relative",
    background: "linear-gradient(180deg, #3D40D5 0%, #0003AC 100%)",
    borderRadius: "24px",
    height: "100%",
  },
}));

export default function PlatformLayout({ children }) {
  const { pathname } = useLocation();
  const { auth } = React.useContext(AuthContext);
  const queryClient = useQueryClient();
  const user = auth ? queryClient.getQueryData(["user"])?.data.users : null;
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const { handleEventTrack } = useMixpanel();
  const { menuLogoLink, coursesLink, pathsLink } = mixpanelStudentPlatform;

  const toggleDrawer = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    localStorage.removeItem("redirectPath");
  }, []);

  return (
    <Container disableGutters maxWidth={false} className={classes.container}>
      <Container maxWidth="lg" className={classes.container__inner}>
        <Box className={classes.wrapper}>
          <Drawer className={classes.drawer} variant="permanent" open={open}>
            <DrawerHeader
              sx={{ justifyContent: open ? "space-between" : "center" }}
            >
              <Link
                to="/"
                style={{
                  display: open ? "flex" : "none",
                  transform: "translateZ(1px)",
                }}
                onClick={(e) => handleEventTrack(e, menuLogoLink)}
              >
                <DrawerLogo />
              </Link>
              <IconButton
                disableRipple
                onClick={toggleDrawer}
                className={classes.toggle}
                sx={{
                  transform: `scaleX(${open ? 1 : -1})`,
                  padding: "0.5rem 0 0",
                  backgroundColor: "transparent",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="33"
                  viewBox="0 0 32 33"
                  fill="none"
                >
                  <path
                    d="M18.6667 22.1399V18.1399H24V15.4732H18.6667V11.4732L13.3333 16.8066L18.6667 22.1399ZM8 27.4732H10.6667V6.13989H8V27.4732Z"
                    fill="white"
                  />
                </svg>
              </IconButton>
            </DrawerHeader>
            <Divider
              sx={{
                margin: `0 1.5rem`,
                borderColor: theme.palette["white-10"],
                borderBottomWidth: "1px",
              }}
            />
            {user ? (
              <>
                <Box
                  component={NavLink}
                  to={"/dashboard"}
                  className={classes.user}
                >
                  <User
                    fullName={user.fullName}
                    rating={user.rating}
                    icon={user.profilePhotoUrl}
                    isContentVisible={open}
                    iconWhite={true}
                  />
                </Box>
                <Divider
                  sx={{
                    margin: `0 1.5rem`,
                    borderColor: theme.palette["white-10"],
                    borderBottomWidth: "1px",
                  }}
                />
              </>
            ) : null}
            <List sx={{ padding: "1rem 0 1.5rem" }} className={classes.list}>
              <ListItem
                className={classes.list__item}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  disableRipple
                  className={classes.list__link}
                  component={NavLink}
                  to="/courses"
                  onClick={(e) => {
                    if (pathname === "/courses/all-courses") e.preventDefault();
                    handleEventTrack(e, coursesLink);
                  }}
                  sx={{
                    padding: "0 1.5rem",
                    minHeight: 36,
                    justifyContent: open ? "initial" : "center",
                  }}
                >
                  <ListItemIcon
                    className={classes.list__icon}
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <ArchiveIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.list__text}
                    primary="Courses"
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? "flex" : "none",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                className={classes.list__item}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  disableRipple
                  className={classes.list__link}
                  component={NavLink}
                  to="/paths"
                  onClick={(e) => handleEventTrack(e, pathsLink)}
                  sx={{
                    padding: "0 1.5rem",
                    minHeight: 36,
                    justifyContent: open ? "initial" : "center",
                  }}
                >
                  <ListItemIcon
                    className={classes.list__icon}
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <ConversionIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.list__text}
                    primary="Paths"
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? "flex" : "none",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                className={classes.list__item}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  disableRipple
                  className={classes.list__link}
                  component={NavLink}
                  to="/rating"
                  onClick={(e) => handleEventTrack(e, pathsLink)}
                  sx={{
                    padding: "0 1.5rem",
                    minHeight: 36,
                    justifyContent: open ? "initial" : "center",
                  }}
                >
                  <ListItemIcon
                    className={classes.list__icon}
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <RatingIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.list__text}
                    primary="Rating"
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? "flex" : "none",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem
                className={classes.list__item}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton
                  disableRipple
                  className={classes.list__link}
                  component={NavLink}
                  to="/posts"
                  onClick={(e) => handleEventTrack(e, pathsLink)}
                  sx={{
                    padding: "0 1.5rem",
                    minHeight: 36,
                    justifyContent: open ? "initial" : "center",
                  }}
                >
                  <ListItemIcon
                    className={classes.list__icon}
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2 5C2 3.89543 2.89543 3 4 3H12C13.1046 3 14 3.89543 14 5V15C14 16.1046 14.8954 17 16 17H4C2.89543 17 2 16.1046 2 15V5ZM5 6H11V10H5V6ZM11 12H5V14H11V12Z"
                        fill="white"
                      />
                      <path
                        d="M15 7H16C17.1046 7 18 7.89543 18 9V14.5C18 15.3284 17.3284 16 16.5 16C15.6716 16 15 15.3284 15 14.5V7Z"
                        fill="white"
                      />
                    </svg>
                  </ListItemIcon>
                  <ListItemText
                    className={classes.list__text}
                    primary="Blog"
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? "flex" : "none",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider
              sx={{
                margin: `0 1.5rem`,
                borderColor: theme.palette["white-10"],
                borderBottomWidth: "1px",
              }}
              hidden={!open}
            />
          </Drawer>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
            <Header user={user} />
            <Box
              component={"main"}
              className={classes.outlet}
              sx={{ flexGrow: 1 }}
            >
              {children}
            </Box>
            <Box className={classes.footer}>
              <Footer />
            </Box>
          </Box>
        </Box>
      </Container>
    </Container>
  );
}
